import React from 'react';

import Responsive from '../../../common/components/responsive/Responsive';
import {
  Marker,
  DraggableMarker,
  MapCenterDraggableMarker,
  MARKER_ANCHOR_OPTIONS,
} from '../marker/Marker';
import POIIcon from '../poi-icon/POIIcon';
import { CountryBoundariesTransparentLayer } from '../layers/country-boundaries-transparent-layer/CountryBoundariesTransparentLayer';
import { MaskLayer } from '../layers/mask-layer/MaskLayer';
import InteractiveLayers from '../layers/interactive-layers/InteractiveLayers';
import RoutablePointMarkerIcon from '../routable-point-marker-icon/RoutablePointMarkerIcon';

import { useMapItemEdit } from './useMapItemEdit';
import Overlay from '../../../common/components/overlay/Overlay.tsx';
import GuidanceLabel from '../guidance-label/GuidanceLabel.tsx';
import { sidepanelPadding } from '../../utils/hooks/useMapPadding.ts';
import { useSelector } from 'react-redux';
import { selectHeight as selectWizardHeight } from '../../../common/store/wizard/index.ts';

const MapItemEdit = () => {
  const {
    item,
    pointItem,
    locationSelectable,
    markerLocation,
    maxMoveDistancePolygon,
    routablePoints,
    editingRoutablePoints,
    isMobile,
    handleMarkerDragEnd,
    handlePointDragEnd,
    handlePointDragStart,
    correctMarkerCoordinates,
    correctActiveRoutablePointCoordinates,
    overlayParams,
  } = useMapItemEdit();

  const wizardHeight = useSelector(selectWizardHeight);

  if (!item) {
    return null;
  }

  return (
    <>
      <CountryBoundariesTransparentLayer />
      {overlayParams && (
        <Overlay
          className="map__overlay"
          style={{ paddingBottom: wizardHeight || 0 }}
        >
          <GuidanceLabel
            className="map-item-add__guidance-label"
            icon={overlayParams.icon}
            style={isMobile ? null : { marginLeft: sidepanelPadding.left }}
          >
            {overlayParams.text}
          </GuidanceLabel>
        </Overlay>
      )}
      <InteractiveLayers />
      {pointItem && (
        <>
          <Marker
            key={pointItem.properties.id}
            id={locationSelectable ? 'old-position-marker' : ''}
            location={
              locationSelectable
                ? pointItem.geometry.coordinates
                : markerLocation || pointItem.geometry.coordinates
            }
          >
            <POIIcon poi={pointItem} gray={locationSelectable} />
          </Marker>
          {locationSelectable && (
            <>
              <Responsive>
                <MapCenterDraggableMarker
                  id="new-position-marker"
                  key={pointItem.properties.id}
                  location={markerLocation || pointItem.geometry.coordinates}
                  onDragEnd={handleMarkerDragEnd}
                  beforeCoordinatesUpdate={correctMarkerCoordinates}
                >
                  <POIIcon poi={pointItem} />
                </MapCenterDraggableMarker>
              </Responsive>
              <Responsive mobile={false}>
                <DraggableMarker
                  id="new-position-marker"
                  key={pointItem.properties.id}
                  location={markerLocation || pointItem.geometry.coordinates}
                  // TODO: chech when it was added, it's not used now
                  // label={pointItem.properties.feature_name}
                  onDragEnd={handleMarkerDragEnd}
                  beforeCoordinatesUpdate={correctMarkerCoordinates}
                >
                  <POIIcon poi={pointItem} />
                </DraggableMarker>
              </Responsive>
              <MaskLayer polygon={maxMoveDistancePolygon} />
            </>
          )}
        </>
      )}

      {routablePoints &&
        routablePoints.map((point, idx) => {
          if (!point.location) {
            return null;
          }

          if (!editingRoutablePoints || (isMobile && !point.active)) {
            return (
              <Marker
                key={`routable-point-marker-${idx}`}
                location={point.location}
                initialAnchor={MARKER_ANCHOR_OPTIONS.center}
              >
                <RoutablePointMarkerIcon label={`${idx + 1}`} />
              </Marker>
            );
          }

          if (isMobile && point.active) {
            return (
              <MapCenterDraggableMarker
                key={`routable-point-marker-${idx}`}
                location={point.location}
                initialAnchor={MARKER_ANCHOR_OPTIONS.center}
                onDragEnd={(location) => handlePointDragEnd(idx, location)}
                beforeCoordinatesUpdate={correctActiveRoutablePointCoordinates}
              >
                <RoutablePointMarkerIcon active={true} label={`${idx + 1}`} />
              </MapCenterDraggableMarker>
            );
          }

          return (
            <DraggableMarker
              key={`routable-point-marker-${idx}`}
              location={point.location}
              initialAnchor={MARKER_ANCHOR_OPTIONS.center}
              onDragStart={() => handlePointDragStart(idx)}
              onDragEnd={(location) => handlePointDragEnd(idx, location)}
              beforeCoordinatesUpdate={correctActiveRoutablePointCoordinates}
            >
              <RoutablePointMarkerIcon
                active={point.active}
                label={`${idx + 1}`}
              />
            </DraggableMarker>
          );
        })}
    </>
  );
};

export default MapItemEdit;
